import React from "react";
import { Box, Collapse, Divider, LinearProgress, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import IconInfo from "@material-ui/icons/Info";
import { FormattedMessage } from "react-intl";
import ActionDialog from "./ActionDialog";
import { passwordRules } from "../../util/passwordStrength";

const strengthValues = {
  STRONG: 85,
  MEDIUM: 40,
};

const requirementsIds = [
  "app.password.indicator.requirements.hasMinLength",
  "app.password.indicator.requirements.hasUpperAndLowerCase",
  "app.password.indicator.requirements.hasNumbers",
  "app.password.indicator.requirements.hasSymbol",
  "app.password.indicator.requirements.hasPersonalInfo",
  "app.password.indicator.requirements.hasNoSequences",
  "app.password.indicator.requirements.hasNoRepeatedCharacters",
];

const StrengthBar = ({ password, focused }) => {
  const classes = useProgressStyles();
  const [isOpenDialog, setIsOpenDialog] = React.useState(false);

  const strengthPercent = React.useMemo(() => {
    const ruleCount = passwordRules.length;
    const score = passwordRules.filter((passwordRule) => passwordRule.check(password)).length;
    return (score / ruleCount) * 100;
  }, [password]);

  const progressColor = React.useMemo(() => {
    if (strengthPercent > strengthValues.STRONG) return classes.strong;
    if (strengthPercent > strengthValues.MEDIUM) return classes.medium;
    return classes.weak;
  }, [classes.medium, classes.strong, classes.weak, strengthPercent]);

  return (
    <Collapse
      in={focused}
      timeout="auto"
      mountOnEnter
      unmountOnExit
    >
      <Box
        className={classes.indicatorStrength}
        onClick={() => setIsOpenDialog(true)}
      >
        <Typography
          variant="caption"
          color="textSecondary"
          align="right"
          gutterBottom
        >
          <FormattedMessage id="app.password.indicator" />
        </Typography>
        <IconInfo
          className={classes.icon}
          fontSize="small"
        />
      </Box>
      <LinearProgress
        variant="determinate"
        value={strengthPercent}
        classes={{
          root: classes.root,
          bar1Determinate: `${classes.bar1Determinate} ${progressColor}`,
        }}
      />
      <ActionDialog
        title="app.password.indicator"
        open={isOpenDialog}
        onClose={() => setIsOpenDialog(false)}
        enableConfirmButton={false}
        maxWidth="xs"
        backgroundPrimaryColor
      >
        <Typography
          variant="body2"
          paragraph
          className={classes.marginTop2}
        >
          <FormattedMessage id="app.password.indicator.strongPassword" />
        </Typography>
        <Typography
          variant="body2"
          paragraph
        >
          <FormattedMessage id="app.password.indicator.colorIndicator" />
        </Typography>
        <Typography
          variant="body2"
          paragraph
        >
          <FormattedMessage id="app.password.indicator.recommendation" />
        </Typography>
        <Typography
          variant="body2"
          paragraph
        >
          <FormattedMessage id="app.password.indicator.requirements" />
        </Typography>

        <Box
          component="ul"
          className={classes.ul}
        >
          {requirementsIds.map((message) => (
            <Box
              key={message}
              component="li"
              className={classes.li}
            >
              <Typography
                variant="body2"
                component="span"
              >
                <FormattedMessage id={message} />
              </Typography>
            </Box>
          ))}
        </Box>
        <Divider className={classes.divider} />
        <Typography
          variant="body2"
          paragraph
          component={"i"}
        >
          <FormattedMessage id="app.password.indicator.noReused" />
        </Typography>
      </ActionDialog>
    </Collapse>
  );
};

export default StrengthBar;

const useProgressStyles = makeStyles((theme) => ({
  root: {
    marginTop: 4,
    marginBottom: 4,
  },
  bar1Determinate: {
    borderRadius: 4,
  },
  weak: {
    backgroundColor: theme.palette.error.main,
  },
  medium: {
    backgroundColor: theme.palette.warning.main,
  },
  strong: {
    backgroundColor: theme.palette.success.main,
  },
  indicatorStrength: {
    cursor: "pointer",
    display: "flex",
    alignContent: "center",
    justifyContent: "flex-start",
    transition: "opacity 0.3s ease",
    "&:hover": {
      opacity: 0.7,
    },
  },
  icon: {
    marginLeft: 4,
  },
  ul: {
    paddingLeft: 16,
    margin: 0,
  },
  li: {
    marginBottom: theme.spacing(1),
  },
  marginTop2: {
    marginTop: theme.spacing(2),
  },
  divider:{
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  }
}));
